import { useSyncExternalStore } from 'react'

/**
 * Chrome / Edge / Samsung Internet fire `beforeinstallprompt` once, soon after load, when the site is installable. The listener is
 * attached when this module is first evaluated (with the app bundle, before React hydrates) so the event is never missed; its
 * default (Chrome's own mini bar) is prevented: CADINU shows its own quiet line and the More entry instead.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

type Snapshot = { canPrompt: boolean; installed: boolean }
let deferred: BeforeInstallPromptEvent | null = null
let snapshot: Snapshot = { canPrompt: false, installed: false }
const listeners = new Set<() => void>()

function set(next: Partial<Snapshot>) {
  snapshot = { ...snapshot, ...next }
  listeners.forEach((l) => l())
}

function onBeforeInstallPrompt(e: Event) {
  e.preventDefault()
  deferred = e as BeforeInstallPromptEvent
  set({ canPrompt: true })
}

function onInstalled() {
  deferred = null
  set({ canPrompt: false, installed: true })
}

let attached = false
export function attachInstallPrompt(win: Window) {
  if (attached) return
  attached = true
  win.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
  win.addEventListener('appinstalled', onInstalled)
}
if (typeof window !== 'undefined') attachInstallPrompt(window)

const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}
const getSnapshot = () => snapshot
const getServerSnapshot = () => snapshot

export function useInstallPrompt(): Snapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

/** Shows the browser's install dialog (must run inside a tap). 'unavailable' when the browser offered no prompt. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const e = deferred
  if (!e) return 'unavailable'
  deferred = null // a prompt can be used once
  set({ canPrompt: false })
  await e.prompt()
  const { outcome } = await e.userChoice
  if (outcome === 'accepted') set({ installed: true })
  return outcome
}

export function __resetInstallPromptForTests() {
  deferred = null
  snapshot = { canPrompt: false, installed: false }
  listeners.forEach((l) => l())
}
